import { WindowSummary } from './flock-telemetry.models';
import { toUtcIso } from './flock-telemetry.utils';

/**
 * "6:25 AM" in local time. Built by hand because the NativeScript iOS runtime
 * ignores toLocaleTimeString options and prints "06:25:42 GMT-0500 (CDT)".
 */
export function clockTime(at: Date): string {
  const hours = at.getHours();
  const minutes = `${at.getMinutes()}`.padStart(2, '0');
  return `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
}

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Long zone names whose initials are NOT the conventional abbreviation
 * ("Coordinated Universal Time" would otherwise print "CUT"). Keys are
 * lowercased. Everything else (US, European, Australian, "India Standard
 * Time", ...) already initials to its standard short form.
 */
const LONG_ZONE_ABBREVIATIONS: Record<string, string> = {
  'coordinated universal time': 'UTC',
  'universal time': 'UTC',
  'universal time coordinated': 'UTC',
};

/**
 * Pulls a short zone token like "CDT" out of a runtime date-to-string output
 * that ends in a parenthesized zone descriptor: either
 * "06:25:42 GMT-0500 (CDT)" (NativeScript iOS `toLocaleTimeString()`, which
 * already prints the short abbreviation -- see `clockTime`'s comment) or
 * "Wed Sep 23 2026 17:48:05 GMT-0500 (Central Daylight Time)" (Node/V8
 * `toString()`, used by Vitest and as a fallback). A short all-caps group is
 * used as-is; a long, multi-word name is abbreviated to its initials so both
 * runtimes agree on the same "CDT" shape. Pure string parsing -- no Intl,
 * no Date -- so it is trivially testable with literal fixtures.
 */
export function extractTimeZoneAbbreviation(dateString: string): string {
  const match = /\(([^)]+)\)\s*$/.exec(dateString);
  if (!match) return '';
  const zone = match[1].trim();
  if (/^[A-Z]{2,5}$/.test(zone)) return zone;
  // Offset-only descriptors ("GMT+05:30", "UTC-3") are already the
  // abbreviation; initials would collapse them to a meaningless "G"/"U".
  if (/^(GMT|UTC)([+-]\d{1,2}(:?\d{2})?)?$/.test(zone)) return zone;
  const known = LONG_ZONE_ABBREVIATIONS[zone.toLowerCase()];
  if (known) return known;
  return zone
    .split(/\s+/)
    .filter((word) => /^[A-Za-z]/.test(word))
    .map((word) => word[0].toUpperCase())
    .join('');
}

/**
 * "Sep 23, 5:48 PM CDT": a readable, unambiguous local date+time+zone for
 * user-facing timestamps (WI 34251). Never raw ISO/UTC ("2026-09-23T22:48
 * +00:00") and never the raw device-string form ("17:48:05 GMT-0500 (CDT)").
 * Date getters are already device-local (JS `Date` stores UTC internally;
 * `getMonth`/`getDate`/`getHours` etc. all read back in the device's
 * configured zone), so only the zone abbreviation needs scraping -- tried
 * against `toLocaleTimeString()` first (what NativeScript iOS actually
 * prints), then `toString()` (what Node/Vitest and Android print).
 */
export function localDateTimeLabel(at: Date): string {
  const tz =
    extractTimeZoneAbbreviation(at.toLocaleTimeString()) ||
    extractTimeZoneAbbreviation(at.toString());
  const suffix = tz ? ` ${tz}` : '';
  return `${MONTH_ABBR[at.getMonth()]} ${at.getDate()}, ${clockTime(at)}${suffix}`;
}

/**
 * A last reading older than the longest quota window (7 days) belongs to a
 * window that has already reset at least once: its source stopped reporting,
 * so its percent says nothing about now (WI 38037, Tim's Fable read "last seen
 * 0%" from a 10-03 capture for a month).
 */
export const STALE_READING_UNKNOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** No active reading is not evidence of unused capacity. */
export function noWindowLine(
  win: WindowSummary,
  nextScanLabel: string,
  now: number = Date.now()
): string {
  if (win.staleObservation) {
    const at = new Date(toUtcIso(win.staleObservation.at) ?? '');
    const valid = !Number.isNaN(at.getTime());
    if (valid && now - at.getTime() > STALE_READING_UNKNOWN_MS) {
      return `unknown (no reading since ${MONTH_ABBR[at.getMonth()]} ${at.getDate()})`;
    }
    const t = valid ? ` at ${clockTime(at)}` : '';
    return `no active window (last seen ${Math.round(win.staleObservation.percent)}%${t})`;
  }
  if (win.tokenCount > 0) {
    return `reading pending, next scan ${nextScanLabel}`;
  }
  return 'no active window';
}
